import React, { useEffect, useState } from 'react';
import {
  Typography,
  CardContent,
  CircularProgress,
  Alert as MuiAlert, // Renamed to avoid conflict with Alert interface
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Box,
  useTheme,
  alpha,
  Divider,
  Button,
} from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard, StatsCard } from '../components/layout/EnhancedCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import GameLeagueSelector from '../components/GameLeagueSelector';
import {
  fetchDashboardStats,
  fetchCriticalAlertsSummary,
  selectDashboardStats,
  selectCriticalAlertsSummary,
  selectDashboardStatus,
  selectDashboardError,
  //selectSelectedGame,
  clearDashboardError,
  setSelectedGame,
} from '../store/slices/dashboardSlice';
import { setGame, setLeague, selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';
import type { AppDispatch } from '../store/store';
import { Link } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleIcon from '@mui/icons-material/People';
import ComputerIcon from '@mui/icons-material/Computer';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import DivineOrbIcon from '../components/icons/DivineOrbIcon';
import dashboardService, { type DivineHistoryDashboardStats, type DivHrDashboardStats } from '../services/dashboardService';

// Note: StatCard is now imported from EnhancedCard component

const HomePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const stats = useSelector(selectDashboardStats);
  const criticalAlerts = useSelector(selectCriticalAlertsSummary);
  const status = useSelector(selectDashboardStatus);
  const error = useSelector(selectDashboardError);

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // State for divine history data
  const [divineHistoryData, setDivineHistoryData] = useState<DivineHistoryDashboardStats | null>(null);
  const [divineHistoryLoading, setDivineHistoryLoading] = useState(false);
  const [divineHistoryError, setDivineHistoryError] = useState<string | null>(null);

  // State for Div/Hr dashboard data
  const [divHrData, setDivHrData] = useState<DivHrDashboardStats | null>(null);
  const [divHrLoading, setDivHrLoading] = useState(false);
  const [divHrError, setDivHrError] = useState<string | null>(null);

  useEffect(() => {
    // Clear any previous errors and fetch data when component mounts
    // or when game/league selection changes
    dispatch(clearDashboardError());
    dispatch(fetchDashboardStats());
    dispatch(fetchCriticalAlertsSummary());
  }, [dispatch, selectedGame, selectedLeague]);

  // Fetch divine history data
  useEffect(() => {
    const fetchDivineHistory = async () => {
      setDivineHistoryLoading(true);
      setDivineHistoryError(null);
      try {
        const data = await dashboardService.getDivineHistoryDashboard();
        setDivineHistoryData(data);
      } catch (err) {
        setDivineHistoryError(err instanceof Error ? err.message : 'Failed to fetch divine history');
      } finally {
        setDivineHistoryLoading(false);
      }
    };

    fetchDivineHistory();
  }, [selectedGame, selectedLeague]);

  // Fetch Div/Hr dashboard data
  useEffect(() => {
    const fetchDivHrData = async () => {
      setDivHrLoading(true);
      setDivHrError(null);
      try {
        const data = await dashboardService.getDivHrDashboard();
        setDivHrData(data);
      } catch (err) {
        setDivHrError(err instanceof Error ? err.message : 'Failed to fetch Div/Hr dashboard data');
      } finally {
        setDivHrLoading(false);
      }
    };

    fetchDivHrData();
  }, [selectedGame, selectedLeague]);

  const handleGameChange = (gameCode: string) => {
    // Update both dashboard and filter state
    dispatch(setSelectedGame(gameCode));
    dispatch(setGame(gameCode));
  };

  const handleLeagueChange = (leagueCode: string) => {
    // Update filter state with the new league
    dispatch(setLeague(leagueCode));
  };

  const getSeverityChip = (severity: 'critical' | 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'critical':
        return <Chip icon={<ErrorOutlineIcon />} label='Critical' color='error' size='small' />;
      case 'high':
        return <Chip icon={<WarningAmberIcon />} label='High' color='warning' size='small' />;
      case 'medium':
        return <Chip icon={<InfoOutlinedIcon />} label='Medium' color='info' size='small' />;
      case 'low':
        return <Chip icon={<CheckCircleOutlineIcon />} label='Low' color='success' size='small' />;
      default:
        return <Chip label={severity} size='small' />;
    }
  };

  // Format chart data for recharts
  const formatChartData = (data: DivineHistoryDashboardStats) => {
    return data.historyPoints.map(point => ({
      timestamp: point.timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      quantity: point.cumulativeDivineOrbs,
      fullTimestamp: point.timestamp.toLocaleString(),
    }));
  };

  // Format Div/Hr chart data for recharts
  const formatDivHrChartData = (data: DivHrDashboardStats) => {
    return data.historyPoints.map(point => ({
      timestamp: point.timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      divPerHour: point.divPerHour,
      divineOrbsGained: point.divineOrbsGained,
      fullTimestamp: point.timestamp.toLocaleString(),
    }));
  };

  return (
    <PageLayout>
      <PageHeader title='ExileGuard Dashboard' subtitle="Welcome back! Here's an overview of your botfarm operations" />

      <ContentSection>
        <GameLeagueSelector onGameChange={handleGameChange} onLeagueChange={handleLeagueChange} />
      </ContentSection>

      {status === 'failed' && error && (
        <MuiAlert severity='error' sx={{ mb: 3 }}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </MuiAlert>
      )}

      {/* We don't need a separate heading for the stats since we have the dashboard title */}
      <Grid
        sx={{
          mb: 4,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
        }}
      >
        <div>
          <StatsCard
            title='Characters'
            value={stats?.totalCharacters ?? '-'}
            loading={status === 'loading' && !stats}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
          />
        </div>
        <div>
          <StatsCard
            title='Divine Orbs'
            value={stats?.divineOrbCount ?? '-'}
            loading={status === 'loading' && !stats}
            icon={<DivineOrbIcon size={24} />}
            color={theme.palette.secondary.main}
          />
        </div>
        <div>
          <StatsCard
            title='Active Bots'
            value={stats?.activeBotsCount ?? '-'}
            loading={status === 'loading' && !stats}
            icon={<ComputerIcon />}
            color={theme.palette.info.main}
          />
        </div>
        <div>
          <StatsCard
            title='Active Alerts'
            value={stats?.activeAlertCount ?? '-'}
            loading={status === 'loading' && !stats}
            icon={<CampaignIcon />}
            color={theme.palette.warning.main}
          />
        </div>
      </Grid>

      {/* Divine History Graph Section */}
      <ContentSection>
        <EnhancedCard>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant='h6' component='h2' sx={{ mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DivineOrbIcon size={24} />
                Divine Orb History (Last 24 Hours)
              </Typography>
              {divineHistoryData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: divineHistoryData.lastHourGain > 0 ? theme.palette.success.main : theme.palette.text.secondary,
                      fontWeight: 'bold',
                    }}
                  >
                    + {divineHistoryData.lastHourGain} in last hour
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Total: {divineHistoryData.currentTotal}
                  </Typography>
                </Box>
              )}
            </Box>

            {divineHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : divineHistoryError ? (
              <MuiAlert severity='error' sx={{ mb: 2 }}>
                {divineHistoryError}
              </MuiAlert>
            ) : divineHistoryData && divineHistoryData.historyPoints.length > 0 ? (
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={formatChartData(divineHistoryData)}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='timestamp' tick={{ fontSize: 12 }} interval='preserveStartEnd' />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(label, payload) => {
                        const data = payload?.[0]?.payload;
                        return data?.fullTimestamp || label;
                      }}
                      formatter={(value: number) => [value, 'Divine Orbs']}
                    />
                    <Line
                      type='monotone'
                      dataKey='quantity'
                      stroke={theme.palette.secondary.main}
                      strokeWidth={2}
                      dot={{
                        fill: theme.palette.secondary.main,
                        strokeWidth: 2,
                      }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 4 }}>
                No divine orb history data available for the selected filters.
              </Typography>
            )}
          </CardContent>
        </EnhancedCard>
      </ContentSection>

      {/* Div/Hr Dashboard Graph Section */}
      <ContentSection>
        <EnhancedCard>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant='h6' component='h2' sx={{ mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DivineOrbIcon size={24} />
                Div/Hr Rates (Last 24 Hours)
              </Typography>
              {divHrData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: divHrData.currentDivPerHour > 0 ? theme.palette.success.main : theme.palette.text.secondary,
                      fontWeight: 'bold',
                    }}
                  >
                    Current: {divHrData.currentDivPerHour.toFixed(2)} Div/Hr
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    24h Total: {divHrData.totalDivineOrbsGained24h} Divs
                  </Typography>
                </Box>
              )}
            </Box>

            {divHrLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : divHrError ? (
              <MuiAlert severity='error' sx={{ mb: 2 }}>
                {divHrError}
              </MuiAlert>
            ) : divHrData && divHrData.historyPoints.length > 0 ? (
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={formatDivHrChartData(divHrData)}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='timestamp' tick={{ fontSize: 12 }} interval='preserveStartEnd' />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(label, payload) => {
                        const data = payload?.[0]?.payload;
                        return data?.fullTimestamp || label;
                      }}
                      formatter={(value: number, name: string): [string, string] => {
                        if (name === 'divPerHour') {
                          return [value.toFixed(2), 'Div/Hr'];
                        }
                        return [value.toString(), 'Divine Orbs Gained'];
                      }}
                    />
                    <Line
                      type='monotone'
                      dataKey='divPerHour'
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.primary.main, strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 4 }}>
                No Div/Hr data available for the selected filters.
              </Typography>
            )}
          </CardContent>
        </EnhancedCard>
      </ContentSection>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          mt: 4,
        }}
      >
        <Typography variant='h5' component='h2' gutterBottom sx={{ mb: 0 }}>
          Critical Alerts Summary
        </Typography>
        <Button variant='outlined' size='small' startIcon={<RefreshIcon />} onClick={() => dispatch(fetchCriticalAlertsSummary())}>
          Refresh
        </Button>
      </Box>

      {status === 'loading' && criticalAlerts.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {status !== 'loading' && criticalAlerts.length === 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Typography variant='body1' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No critical alerts to display.
          </Typography>
        </Paper>
      )}
      {criticalAlerts.length > 0 && (
        <EnhancedCard>
          <CardContent sx={{ p: 0 }}>
            <List sx={{ p: 0 }}>
              {criticalAlerts.map(alert => (
                <ListItem
                  key={alert.id}
                  divider
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderLeft: `4px solid ${(() => {
                      switch (alert.severity) {
                        case 'critical':
                          return theme.palette.error.main;
                        case 'high':
                          return theme.palette.warning.main;
                        case 'medium':
                          return theme.palette.info.main;
                        case 'low':
                          return theme.palette.success.main;
                        default:
                          return theme.palette.grey[500];
                      }
                    })()}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
                    {(() => {
                      switch (alert.severity) {
                        case 'critical':
                          return <ErrorOutlineIcon sx={{ color: theme.palette.error.main }} />;
                        case 'high':
                          return <WarningAmberIcon sx={{ color: theme.palette.warning.main }} />;
                        case 'medium':
                          return <InfoOutlinedIcon sx={{ color: theme.palette.info.main }} />;
                        case 'low':
                          return <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />;
                        default:
                          return <InfoOutlinedIcon />;
                      }
                    })()}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant='subtitle2'>{alert.title}</Typography>
                        {getSeverityChip(alert.severity as any)}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                          {alert.description}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {new Date(alert.timestamp).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button variant='outlined' size='small' startIcon={<RefreshIcon />} component={Link} to='/alerts'>
              View All Alerts
            </Button>
          </Box>
        </EnhancedCard>
      )}
    </PageLayout>
  );
};

export default HomePage;
